import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { FaRegUser } from "react-icons/fa6";
import { useDispatch } from "react-redux";
import { logoutUser } from "../../redux/actions/action";
import { persistStore } from "redux-persist";
import store from "../../redux/store/store";
import ProtectedRoute from "../common/ProtechedRoute";
import { useSelector } from "react-redux";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(255, 255, 255, .05)",
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

const LeftSidebarMenu = ({ toggleActive }) => {
  const [expanded, setExpanded] = useState("panel1");
  const dispatch = useDispatch();
  const location = useLocation();

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const isActiveLink = (path) => (location.pathname === path ? "active" : "");

  const [isDark, setIsDark] = useState(false);
  const auth = useSelector((state) => state.auth); // get auth from redux store
  const user = auth?.admin; // or auth?.user depending on your reducer

  useEffect(() => {
    // Only check or set the theme if we're on the beauty-salon page.
    if (location.pathname === "/dashboard/beauty-salon/") {
      const storedTheme = localStorage.getItem("beautySalonSidebarTheme");
      if (storedTheme) {
        setIsDark(storedTheme === "dark-theme");
      } else {
        // Default to dark theme and persist it in localStorage
        setIsDark(true);
        localStorage.setItem("beautySalonSidebarTheme", "dark-theme");
      }
    } else {
      // For other pages, do not use localStorage for the theme
      setIsDark(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    dispatch(logoutUser());
    persistStore(store).purge(); // 🔹 purge here instead of action.js
    window.location.href = "/authentication/sign-in/";
  };

  return (
    <Box
      className={`leftSidebarDark hide-for-horizontal-nav ${
        location.pathname === "/dashboard/beauty-salon/" && isDark
          ? "dark-theme"
          : ""
      }`}
    >
      <Box className="left-sidebar-menu">
        <Box className="logo">
          <Link to="/dashboard/ecommerce/">
            <img
              src="/images/logo-iconn.png"
              alt="logo-icon"
              width={26}
              height={26}
            />
            <Typography component={"span"}>Bharat Online</Typography>
          </Link>
        </Box>

        <Box className="burger-menu" onClick={toggleActive}>
          <Typography component={"span"} className="top-bar"></Typography>
          <Typography component={"span"} className="middle-bar"></Typography>
          <Typography component={"span"} className="bottom-bar"></Typography>
        </Box>

        <Box className="sidebar-inner">
          <Box className="sidebar-menu">
            <Typography
              className="sub-title"
              sx={{
                display: "block",
                fontWeight: "500",
                textTransform: "uppercase",
              }}
            >
              MAIN
            </Typography>

            <Accordion
              expanded={expanded === "panel1"}
              onChange={handleChange("panel1")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel1d-content"
                id="panel1d-header"
              >
                <i className="material-symbols-outlined">dashboard</i>
                <Typography component={"span"} className="title">
                  Dashboard
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/dashboard/ecommerce"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/ecommerce",
                      )}`}
                    >
                      eCommerce
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/dashboard/crm/"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/crm/",
                      )}`}
                    >
                      CRM
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/dashboard/project-management/"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/project-management/",
                      )}`}
                    >
                      Project Management
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/dashboard/lms/"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/lms/",
                      )}`}
                    >
                      LMS
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/dashboard/helpdesk/"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/helpdesk/",
                      )}`}
                    >
                      HelpDesk{" "}
                      <Typography component={"span"} className="trezo-badge">
                        Hot
                      </Typography>
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/dashboard/finance/"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/finance/",
                      )}`}
                    >
                      Finance
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/dashboard/hrm/"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/hrm/",
                      )}`}
                    >
                      HRM
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/dashboard/social-media/"
                      className={`sidemenu-link ${isActiveLink(
                        "/dashboard/social-media/",
                      )}`}
                    >
                      Social Media
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Typography
              className="sub-title"
              sx={{
                display: "block",
                fontWeight: "500",
                textTransform: "uppercase",
              }}
            >
              PAGES
            </Typography>

            <Accordion
              expanded={expanded === "panel6"}
              onChange={handleChange("panel6")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel6d-content"
                id="panel6d-header"
              >
                <i className="material-symbols-outlined">handshake</i>
                <Typography component={"span"} className="title">
                  Business
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <ProtectedRoute
                    user={user}
                    permission="readBusinessTypeListManagement"
                  >
                    {/* <li className="sidemenu-item">
                      <Link
                        to="/business/businessTypeList/"
                        className={`sidemenu-link ${isActiveLink(
                          "/business/businessTypeList/"
                        )}`}
                      >
                        Onboard Business
                      </Link>
                    </li> */}
                  </ProtectedRoute>
                  <ProtectedRoute
                    user={user}
                    permission="readBusinessListManagement"
                  >
                    {/* <li className="sidemenu-item">
                      <Link
                        to="/business/businessList/"
                        className={`sidemenu-link ${isActiveLink(
                          "/business/businessList/",
                        )}`}
                      >
                        Business List
                      </Link>
                    </li> */}
                    <li className="sidemenu-item">
                      <Link
                        to="/business/businessTypeList/"
                        className={`sidemenu-link ${isActiveLink(
                          "/business/businessTypeList/",
                        )}`}
                      >
                        Business List
                      </Link>
                    </li>
                  </ProtectedRoute>
                  <ProtectedRoute
                    user={user}
                    permission="readBusinessTypeListManagement"
                  >
                    <li className="sidemenu-item">
                      <Link
                        to="/master/businessTypeList"
                        className={`sidemenu-link ${isActiveLink(
                          "/master/businessTypeList",
                        )}`}
                      >
                        Business Type List
                      </Link>
                    </li>
                  </ProtectedRoute>
                  <ProtectedRoute
                    user={user}
                    permission="readCategoryListManagement"
                  >
                    <li className="sidemenu-item">
                      <Link
                        to="/master/category-list/"
                        className={`sidemenu-link ${isActiveLink(
                          "/master/category-list/",
                        )}`}
                      >
                        Category List
                      </Link>
                    </li>
                  </ProtectedRoute>
                  <ProtectedRoute
                    user={user}
                    permission="readSubcategoryListManagement"
                  >
                    <li className="sidemenu-item">
                      <Link
                        to="/master/subcategory-list/"
                        className={`sidemenu-link ${isActiveLink(
                          "/master/subcategory-list/",
                        )}`}
                      >
                        Sub-category List
                      </Link>
                    </li>
                  </ProtectedRoute>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel25"}
              onChange={handleChange("panel25")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel25d-content"
                id="panel25d-header"
              >
                <i className="material-symbols-outlined">chat</i>
                <Typography component={"span"} className="title">
                  WhatsApp Business
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/whatsapp/plans"
                      className={`sidemenu-link ${isActiveLink(
                        "/whatsapp/plans",
                      )}`}
                    >
                      Plan Management
                    </Link>
                  </li>
                  <li className="sidemenu-item">
                    <Link
                      to="/whatsapp/wallet"
                      className={`sidemenu-link ${isActiveLink(
                        "/whatsapp/wallet",
                      )}`}
                    >
                      Wallet Dashboard
                    </Link>
                  </li>
                  <li className="sidemenu-item">
                    <Link
                      to="/whatsapp/pricing"
                      className={`sidemenu-link ${isActiveLink(
                        "/whatsapp/pricing",
                      )}`}
                    >
                      Pricing Config
                    </Link>
                  </li>
                  <li className="sidemenu-item">
                    <Link
                      to="/whatsapp/billing"
                      className={`sidemenu-link ${isActiveLink(
                        "/whatsapp/billing",
                      )}`}
                    >
                      Billing Dashboard
                    </Link>
                  </li>
                  <li className="sidemenu-item">
                    <Link
                      to="/whatsapp/revenue"
                      className={`sidemenu-link ${isActiveLink(
                        "/whatsapp/revenue",
                      )}`}
                    >
                      Revenue Tracking
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel5"}
              onChange={handleChange("panel5")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel5d-content"
                id="panel5d-header"
              >
                <i className="material-symbols-outlined">shopping_cart</i>
                <Typography component={"span"} className="title">
                  eCommerce
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/products-grid/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/products-grid/",
                      )}`}
                    >
                      Products Grid
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/products-list/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/products-list/",
                      )}`}
                    >
                      Products List
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/products-list/details/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/products-list/details/",
                      )}`}
                    >
                      Product Details
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/create-product/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/create-product/",
                      )}`}
                    >
                      Create Product
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/edit-product/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/edit-product/",
                      )}`}
                    >
                      Edit Product
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/cart/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/cart/",
                      )}`}
                    >
                      Cart
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/checkout/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/checkout/",
                      )}`}
                    >
                      Checkout
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/orders/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/orders/",
                      )}`}
                    >
                      Orders
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/orders/details/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/orders/details/",
                      )}`}
                    >
                      Order Details
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/orders/create/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/orders/create/",
                      )}`}
                    >
                      Create Order
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/orders/tracking/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/orders/tracking/",
                      )}`}
                    >
                      Order Tracking
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/customers/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/customers/",
                      )}`}
                    >
                      Customers
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/customers/details/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/customers/details/",
                      )}`}
                    >
                      Customer Details
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/categories/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/categories/",
                      )}`}
                    >
                      Categories
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/sellers/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/sellers/",
                      )}`}
                    >
                      Sellers
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/sellers/details/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/sellers/details/",
                      )}`}
                    >
                      Seller Details
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/sellers/create/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/sellers/create/",
                      )}`}
                    >
                      Create Seller
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/reviews/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/reviews/",
                      )}`}
                    >
                      Reviews
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/ecommerce/refunds/"
                      className={`sidemenu-link ${isActiveLink(
                        "/ecommerce/refunds/",
                      )}`}
                    >
                      Refunds
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel24"}
              onChange={handleChange("panel24")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel6d-content"
                id="panel6d-header"
              >
                <i className="material-symbols-outlined">handshake</i>
                <Typography component={"span"} className="title">
                  CRM
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/crm/contacts/"
                      className={`sidemenu-link ${isActiveLink(
                        "/crm/contacts/",
                      )}`}
                    >
                      Contacts
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/crm/customers/"
                      className={`sidemenu-link ${isActiveLink(
                        "/crm/customers/",
                      )}`}
                    >
                      Customers
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/crm/leads/"
                      className={`sidemenu-link ${isActiveLink("/crm/leads/")}`}
                    >
                      Leads
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/crm/deals/"
                      className={`sidemenu-link ${isActiveLink("/crm/deals/")}`}
                    >
                      Deals
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel7"}
              onChange={handleChange("panel7")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel7d-content"
                id="panel7d-header"
              >
                <i className="material-symbols-outlined">description</i>
                <Typography component={"span"} className="title">
                  Project Management
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/project-management/project-overview/"
                      className={`sidemenu-link ${isActiveLink(
                        "/project-management/project-overview/",
                      )}`}
                    >
                      Project Overview
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/project-management/projects-list/"
                      className={`sidemenu-link ${isActiveLink(
                        "/project-management/projects-list/",
                      )}`}
                    >
                      Projects List
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/project-management/create-project/"
                      className={`sidemenu-link ${isActiveLink(
                        "/project-management/create-project/",
                      )}`}
                    >
                      Create Project
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/project-management/clients/"
                      className={`sidemenu-link ${isActiveLink(
                        "/project-management/clients/",
                      )}`}
                    >
                      Clients
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/project-management/teams/"
                      className={`sidemenu-link ${isActiveLink(
                        "/project-management/teams/",
                      )}`}
                    >
                      Teams
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/project-management/kanban-board/"
                      className={`sidemenu-link ${isActiveLink(
                        "/project-management/kanban-board/",
                      )}`}
                    >
                      Kanban Board
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/project-management/users/"
                      className={`sidemenu-link ${isActiveLink(
                        "/project-management/users/",
                      )}`}
                    >
                      Users
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel8"}
              onChange={handleChange("panel8")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel8d-content"
                id="panel8d-header"
              >
                <i className="material-symbols-outlined">auto_stories</i>
                <Typography component={"span"} className="title">
                  LMS
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/lms/courses/"
                      className={`sidemenu-link ${isActiveLink(
                        "/lms/courses/",
                      )}`}
                    >
                      Courses List
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/lms/courses/details/"
                      className={`sidemenu-link ${isActiveLink(
                        "/lms/courses/details/",
                      )}`}
                    >
                      Course Details
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/lms/courses/lesson-preview/"
                      className={`sidemenu-link ${isActiveLink(
                        "/lms/courses/lesson-preview/",
                      )}`}
                    >
                      Lesson Preview
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/lms/create-course/"
                      className={`sidemenu-link ${isActiveLink(
                        "/lms/create-course/",
                      )}`}
                    >
                      Create Course
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/lms/edit-course/"
                      className={`sidemenu-link ${isActiveLink(
                        "/lms/edit-course/",
                      )}`}
                    >
                      Edit Course
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/lms/instructors/"
                      className={`sidemenu-link ${isActiveLink(
                        "/lms/instructors/",
                      )}`}
                    >
                      Instructors
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel9"}
              onChange={handleChange("panel9")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel9d-content"
                id="panel9d-header"
              >
                <i className="material-symbols-outlined">support</i>
                <Typography component={"span"} className="title">
                  HelpDesk
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/helpdesk/tickets/"
                      className={`sidemenu-link ${isActiveLink(
                        "/helpdesk/tickets/",
                      )}`}
                    >
                      Tickets
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/helpdesk/tickets/details/"
                      className={`sidemenu-link ${isActiveLink(
                        "/helpdesk/tickets/details/",
                      )}`}
                    >
                      Ticket Details
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/helpdesk/agents/"
                      className={`sidemenu-link ${isActiveLink(
                        "/helpdesk/agents/",
                      )}`}
                    >
                      Agents
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/helpdesk/reports/"
                      className={`sidemenu-link ${isActiveLink(
                        "/helpdesk/reports/",
                      )}`}
                    >
                      Reports
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel50"}
              onChange={handleChange("panel50")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel50d-content"
                id="panel50d-header"
              >
                <i className="material-symbols-outlined">campaign</i>
                <Typography component={"span"} className="title">
                  Marketing
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <ProtectedRoute
                    user={user}
                    permission="readAdvertisementManagement"
                  >
                    <li className="sidemenu-item">
                      <Link
                        to="/marketing/dashboard"
                        className={`sidemenu-link ${isActiveLink("/marketing/dashboard")}`}
                      >
                        Marketing Dashboard
                      </Link>
                    </li>
                  </ProtectedRoute>
                  <ProtectedRoute
                    user={user}
                    permission="readAdvertisementManagement"
                  >
                    <li className="sidemenu-item">
                      <Link
                        to="/marketing/advertisements"
                        className={`sidemenu-link ${isActiveLink("/marketing/advertisements")}`}
                      >
                        Advertisements
                      </Link>
                    </li>
                  </ProtectedRoute>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel51"}
              onChange={handleChange("panel51")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel51d-content"
                id="panel51d-header"
              >
                <i className="material-symbols-outlined">dashboard_customize</i>
                <Typography component={"span"} className="title">
                  App Configuration
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/app-config/customer-app-services"
                      className={`sidemenu-link ${isActiveLink("/app-config/customer-app-services")}`}
                    >
                      Customer App Services
                    </Link>
                  </li>
                  <li className="sidemenu-item">
                    <Link
                      to="/app-config/app-theme"
                      className={`sidemenu-link ${isActiveLink("/app-config/app-theme")}`}
                    >
                      App Theme
                    </Link>
                  </li>
                  <li className="sidemenu-item">
                    <Link
                      to="/app-config/app-version"
                      className={`sidemenu-link ${isActiveLink("/app-config/app-version")}`}
                    >
                      App Version &amp; Updates
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel18"}
              onChange={handleChange("panel18")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel18d-content"
                id="panel18d-header"
              >
                <i className="material-symbols-outlined">local_activity</i>
                <Typography component={"span"} className="title">
                  Events
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <ProtectedRoute
                    user={user}
                    permission="readVenueManagementManagement"
                  >
                    <li className="sidemenu-item">
                      <Link
                        to="/venue"
                        className={`sidemenu-link ${isActiveLink("/venue")}`}
                      >
                        Venue
                      </Link>
                    </li>
                  </ProtectedRoute>
                  <ProtectedRoute
                    user={user}
                    permission="readEventCategoryManagement"
                  >
                    <li className="sidemenu-item">
                      <Link
                        to="/events/category"
                        className={`sidemenu-link ${isActiveLink("/events/category")}`}
                      >
                        Category
                      </Link>
                    </li>
                  </ProtectedRoute>
                  <ProtectedRoute
                    user={user}
                    permission="readEventCategoryManagement"
                  >
                    {/* <li className="sidemenu-item">
                      <Link
                        to="/events/"
                        className={`sidemenu-link ${isActiveLink("/events/")}`}
                      >
                        Events Grid
                      </Link>
                    </li> */}
                  </ProtectedRoute>

                  {/* <ProtectedRoute user={user} permission="readEventsListManagement" > */}
                  <li className="sidemenu-item">
                    <Link
                      to="/events/events-list/"
                      className={`sidemenu-link ${isActiveLink(
                        "/events/events-list/",
                      )}`}
                    >
                      Events List
                    </Link>
                  </li>
                  {/* </ProtectedRoute> */}

                  <li className="sidemenu-item">
                    <Link
                      to="/customers/customers-list/"
                      className={`sidemenu-link ${isActiveLink(
                        "/customers/customers-list/",
                      )}`}
                    >
                      Customers
                    </Link>
                  </li>

                  <ProtectedRoute
                    user={user}
                    permission="readEventDetailsManagement"
                  >
                    <li className="sidemenu-item">
                      {/* <Link
                        to="/events/events-details/"
                        className={`sidemenu-link ${isActiveLink(
                          "/events/events-details/"
                        )}`}
                      >
                        Events Details
                      </Link> */}
                    </li>
                  </ProtectedRoute>
                  <ProtectedRoute user={user} permission="readEventsManagement">
                    {/* <li className="sidemenu-item">
                      <Link
                        to="/events/create-an-event/"
                        className={`sidemenu-link ${isActiveLink(
                          "/events/create-an-event/"
                        )}`}
                      >
                        Create An Event
                      </Link>
                    </li> */}
                  </ProtectedRoute>

                  <ProtectedRoute user={user} permission="readEventsManagement">
                    {/* <li className="sidemenu-item">
                      <Link
                        to="/events/edit-an-event/"
                        className={`sidemenu-link ${isActiveLink(
                          "/events/edit-an-event/"
                        )}`}
                      >
                        Edit An Event
                      </Link>
                    </li> */}
                  </ProtectedRoute>

                  <ProtectedRoute
                    user={user}
                    permission="readEventUsersManagement"
                  >
                    <li className="sidemenu-item">
                      <Link
                        to="/events/userslist/"
                        className={`sidemenu-link ${isActiveLink(
                          "/events/userslist/",
                        )}`}
                      >
                        Users
                      </Link>
                    </li>
                  </ProtectedRoute>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel01"}
              onChange={handleChange("panel01")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel01d-content"
                id="panel18d-header"
              >
                <i className="material-symbols-outlined">share</i>
                <Typography component={"span"} className="title">
                  Social
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/social/profile/"
                      className={`sidemenu-link ${isActiveLink("/social/profile/")}`}
                    >
                      Profile
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/social/settings/"
                      className={`sidemenu-link ${isActiveLink("/social/settings/")}`}
                    >
                      Settings
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion
              expanded={expanded === "panel12"}
              onChange={handleChange("panel12")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel12d-content"
                id="panel12d-header"
              >
                <i className="material-symbols-outlined">calculate</i>
                <Typography component={"span"} className="title">
                  Sport
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/finance/wallet/"
                      className={`sidemenu-link ${isActiveLink(
                        "/finance/wallet/",
                      )}`}
                    >
                      Wallet
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/finance/transactions/"
                      className={`sidemenu-link ${isActiveLink(
                        "/finance/transactions/",
                      )}`}
                    >
                      Transactions
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>

            <Typography
              className="sub-title"
              sx={{
                display: "block",
                fontWeight: "500",
                textTransform: "uppercase",
              }}
            >
              OTHERS
            </Typography>
            <Link
              to="/forms/"
              className={`sidebar-menu-link ${isActiveLink("/forms/")}`}
            >
              <i className="material-symbols-outlined">account_circle</i>
              <Typography component={"span"} className="title">
                Webforms
              </Typography>
            </Link>

            <Link
              to="/access-control/"
              className={`sidebar-menu-link ${isActiveLink("/access-control")}`}
            >
              <i>
                <FaRegUser size={18} />
              </i>
              <Typography component={"span"} className="title">
                Access Control
              </Typography>
            </Link>

            <Link
              to="/my-profile/"
              className={`sidebar-menu-link ${isActiveLink("/my-profile/")}`}
            >
              <i className="material-symbols-outlined">account_circle</i>
              <Typography component={"span"} className="title">
                My Profile
              </Typography>
            </Link>

            <Accordion
              expanded={expanded === "panel31"}
              onChange={handleChange("panel31")}
              className="mat-accordion"
            >
              <AccordionSummary
                className="mat-summary"
                aria-controls="panel31d-content"
                id="panel31d-header"
              >
                <i className="material-symbols-outlined">settings</i>
                <Typography component={"span"} className="title">
                  Settings
                </Typography>
              </AccordionSummary>

              <AccordionDetails className="mat-details">
                <ul className="sidebar-sub-menu">
                  <li className="sidemenu-item">
                    <Link
                      to="/settings/account-settings"
                      className={`sidemenu-link ${isActiveLink(
                        "/settings/account-settings",
                      )}`}
                    >
                      Account Settings
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/settings/change-password/"
                      className={`sidemenu-link ${isActiveLink(
                        "/settings/change-password/",
                      )}`}
                    >
                      Change Password
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/settings/connections/"
                      className={`sidemenu-link ${isActiveLink(
                        "/settings/connections/",
                      )}`}
                    >
                      Connections
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/settings/privacy-policy/"
                      className={`sidemenu-link ${isActiveLink(
                        "/settings/privacy-policy/",
                      )}`}
                    >
                      Privacy Policy
                    </Link>
                  </li>

                  <li className="sidemenu-item">
                    <Link
                      to="/settings/terms-conditions/"
                      className={`sidemenu-link ${isActiveLink(
                        "/settings/terms-conditions/",
                      )}`}
                    >
                      Terms & Conditions
                    </Link>
                  </li>
                </ul>
              </AccordionDetails>
            </Accordion>
            <i className="material-symbols-outlined">logout</i>
            <Typography
              component={"span"}
              className="title"
              onClick={handleLogout}
            >
              Logout
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LeftSidebarMenu;
